import { useEffect, useRef, useState } from "react";

import {
  createScrollHandler,
  resetScrollPosition,
  restoreScrollPosition,
} from "../scrollUtils";

export function useNavScrollPersistence(accountMetadataLoaded: boolean) {
  // useState (not useRef) so effects re-run when the element mounts/unmounts.
  // NavBar returns null while auth/permissions load, so a mount-time effect
  // fires before `<nav>` exists and never re-runs; keying the effects on the
  // element itself makes attach/restore follow the DOM.
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null,
  );
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (!scrollContainer) return;

    const ref: React.RefObject<HTMLElement> = { current: scrollContainer };
    const handler = createScrollHandler(ref);
    scrollContainer.addEventListener("scroll", handler);

    return () => {
      scrollContainer.removeEventListener("scroll", handler);
      handler.cancel();
    };
  }, [scrollContainer]);

  useEffect(() => {
    window.addEventListener("beforeunload", resetScrollPosition);
    return () =>
      window.removeEventListener("beforeunload", resetScrollPosition);
  }, []);

  // Wait for nav items to render before restoring — the skeleton has no overflow,
  // so `scrollTop = N` is silently clamped to 0 while the container is short.
  useEffect(() => {
    if (accountMetadataLoaded && scrollContainer && !hasRestoredRef.current) {
      restoreScrollPosition({ current: scrollContainer });
      hasRestoredRef.current = true;
    }
  }, [accountMetadataLoaded, scrollContainer]);

  return setScrollContainer;
}
